/**
 * 文件缩放工具 - 对上传的File对象进行按比例缩放
 * 支持根据最大像素数限制进行智能缩放
 */

// 缩放结果接口
export interface ZoomResult {
  file: File;
  originalWidth: number;
  originalHeight: number;
  scaledWidth: number;
  scaledHeight: number;
  scaleRatio: number;
  totalPixels: number;
}

// 缩放选项接口
export interface ZoomOptions {
  maxPixel: number;
  quality?: number; // 0-1，默认0.9
  format?: string; // 默认'image/jpeg'
  maintainAspectRatio?: boolean; // 默认true
}

/**
 * 计算缩放后的尺寸，保持宽高比
 * @param originalWidth 原始宽度
 * @param originalHeight 原始高度
 * @param maxPixel 最大像素数
 * @returns 缩放后的宽度和高度
 */
export function calculateZoomDimensions(
  originalWidth: number,
  originalHeight: number,
  maxPixel: number,
): { width: number; height: number; scaleRatio: number } {
  const originalPixels = originalWidth * originalHeight;

  // 如果原始像素数小于等于最大像素数，不需要缩放
  if (originalPixels <= maxPixel) {
    return {
      width: originalWidth,
      height: originalHeight,
      scaleRatio: 1,
    };
  }

  // 计算缩放比例：√(maxPixel / originalPixels)
  const scaleRatio = Math.sqrt(maxPixel / originalPixels);

  // 计算缩放后的尺寸
  const scaledWidth = Math.round(originalWidth * scaleRatio);
  const scaledHeight = Math.round(originalHeight * scaleRatio);

  return {
    width: scaledWidth,
    height: scaledHeight,
    scaleRatio,
  };
}

/**
 * 创建图片元素
 * @param file 文件对象
 * @returns Promise<HTMLImageElement>
 */
function createImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        if (img.width <= 0 || img.height <= 0) {
          reject(new Error('图片尺寸无效'));
          return;
        }
        resolve(img);
      };

      img.onerror = () => {
        reject(new Error('图片加载失败'));
      };

      img.src = e.target?.result as string;
    };

    reader.onerror = () => {
      reject(new Error('文件读取失败'));
    };

    reader.readAsDataURL(file);
  });
}

/**
 * 将图片绘制到Canvas并转换为File对象
 * @param img 图片元素
 * @param width 目标宽度
 * @param height 目标高度
 * @param options 缩放选项
 * @returns Promise<File>
 */
function canvasToFile(img: HTMLImageElement, width: number, height: number, options: ZoomOptions): Promise<File> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('无法创建Canvas上下文'));
      return;
    }

    canvas.width = width;
    canvas.height = height;

    // 设置高质量渲染
    ctx.imageSmoothingEnabled = true;
    if (typeof ctx.imageSmoothingQuality === 'string') {
      ctx.imageSmoothingQuality = 'high';
    }

    // 绘制缩放后的图片
    ctx.drawImage(img, 0, 0, width, height);

    // 转换为Blob
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Canvas转换失败'));
          return;
        }

        // 创建新的File对象
        const newFile = new File([blob], img.alt || 'scaled-image', {
          type: options.format || 'image/jpeg',
        });

        resolve(newFile);
      },
      options.format || 'image/jpeg',
      options.quality || 0.9,
    );
  });
}

/**
 * 对上传文件进行缩放操作
 * @param file 原始文件
 * @param options 缩放选项
 * @returns Promise<ZoomResult> 缩放结果
 */
export async function zoomFile(file: File, options: ZoomOptions): Promise<ZoomResult> {
  // 验证文件类型
  if (!file.type.startsWith('image/')) {
    throw new Error('文件必须是图片类型');
  }

  // 验证参数
  if (options.maxPixel <= 0) {
    throw new Error('maxPixel必须大于0');
  }

  try {
    // 创建图片元素
    const img = await createImageFromFile(file);
    const originalWidth = img.width;
    const originalHeight = img.height;

    // 计算缩放尺寸
    const {
      width: scaledWidth,
      height: scaledHeight,
      scaleRatio,
    } = calculateZoomDimensions(originalWidth, originalHeight, options.maxPixel);

    // 如果不需要缩放，直接返回原文件
    if (scaleRatio === 1) {
      return {
        file,
        originalWidth,
        originalHeight,
        scaledWidth,
        scaledHeight,
        scaleRatio,
        totalPixels: originalWidth * originalHeight,
      };
    }

    // 创建缩放后的文件
    const scaledFile = await canvasToFile(img, scaledWidth, scaledHeight, options);

    return {
      file: scaledFile,
      originalWidth,
      originalHeight,
      scaledWidth,
      scaledHeight,
      scaleRatio,
      totalPixels: scaledWidth * scaledHeight,
    };
  } catch (error) {
    throw new Error(`文件缩放失败: ${error instanceof Error ? error.message : '未知错误'}`);
  }
}

/**
 * 批量缩放文件
 * @param files 文件数组
 * @param options 缩放选项
 * @returns Promise<ZoomResult[]> 缩放结果数组
 */
export async function zoomFiles(files: File[], options: ZoomOptions): Promise<ZoomResult[]> {
  const results: ZoomResult[] = [];

  for (const file of files) {
    try {
      const result = await zoomFile(file, options);
      results.push(result);
    } catch (error) {
      console.error(`文件 ${file.name} 缩放失败:`, error);
      // 可以选择跳过失败的文件或者抛出错误
      throw error;
    }
  }

  return results;
}

/**
 * 检查文件是否需要缩放
 * @param file 文件对象
 * @param maxPixel 最大像素数
 * @returns Promise<boolean> 是否需要缩放
 */
export async function needsZoom(file: File, maxPixel: number): Promise<boolean> {
  try {
    const img = await createImageFromFile(file);
    const totalPixels = img.width * img.height;
    return totalPixels > maxPixel;
  } catch {
    return false;
  }
}

/**
 * 获取文件的像素信息
 * @param file 文件对象
 * @returns Promise<{width: number, height: number, totalPixels: number}>
 */
export async function getFilePixelInfo(file: File): Promise<{
  width: number;
  height: number;
  totalPixels: number;
}> {
  const img = await createImageFromFile(file);
  return {
    width: img.width,
    height: img.height,
    totalPixels: img.width * img.height,
  };
}
